"use client"
import * as React from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { ShoppingBag } from "lucide-react"
import ProductDetailDialog from "./ProductDetailDialog"
import Image from "next/image"

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel"

export interface Product {
  id: number;
  name: string;
  price: string;
  image?: string; // This will be the main image for the card
  description: string;
  showDetails?: boolean;
}

export const productsMock: Product[] = [
  {
    id: 1,
    name: "Studio Fix Fluid SPF 15 24HR Matte Foundation",
    price: "$59.99",
    image: "/img1.png",
    showDetails: true,
    description: "A 24-hour buildable, breathable foundation with a natural, soft-matte finish.",
  },
  {
    id: 2,
    name: "New Studio Fix Powder Plus Foundation",
    price: "$59.99",
    image: "/img2.png",
    description: "A one-step powder and foundation that gives skin a smooth, matte finish.",
  },
   {
    id: 3,
    name: "Studio Radiance Serum-Powered™ Foundation",
    price: "$59.99",
    image: "/img3.png",
    description: "A serum-powered foundation that fuses skincare with makeup for a radiant finish.",
  },
  {
    id: 4,
    name: "Studio Radiance Serum-Powered™ Foundation",
    price: "$59.99",
    image: "/img4.png",
    description: "A serum-powered foundation that fuses skincare with makeup for a radiant finish.",
  },
  {
    id: 5,
    name: "Studio Radiance Serum-Powered™ Foundation",
    price: "$59.99",
    image: "/img1.png",
    description: "A serum-powered foundation that fuses skincare with makeup for a radiant finish.",
  },
];

interface ProductShowcaseProps {
  products?: Product[];
}

export default function ProductShowcase({ products }: ProductShowcaseProps) {
  const [selectedProduct, setSelectedProduct] = React.useState<Product | null>(null);
  
  return (
    <>
    <div className="w-full max-w-4xl mx-auto py-8">
      {/* ✨ --- Carousel Implementation --- ✨ 
        The Carousel component is the main wrapper.
        We've added some options here for better UX.
      */}
      { products && products.length > 0 && <Carousel
        opts={{
          align: "start",
          loop: true,
        }}
        className="w-full"
      >
        {/* CarouselContent is the scrollable container */}
         <CarouselContent className="-ml-4">
          { products.map((product) => (
            // Each product is wrapped in a CarouselItem for correct spacing and sizing
            <CarouselItem key={product.id} className="pl-4 md:basis-1/2 lg:basis-1/3">
              <div className="p-1">
                 {/* Your existing Card component fits perfectly inside */}
                <Card className="overflow-hidden bg-background-main">
                <CardContent className="p-0">
                      <div className="relative group">
                        <div className="bg-gray-50 p-8 flex items-center justify-center h-64">
                          <Image
                            src={product.image}
                            alt={product.name}
                            width={200}
                            height={200}
                            className="max-h-full max-w-full object-contain"
                          />
                        </div>
                        {/* The "Show Details" button now triggers the dialog */}
                        <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          {/* ✨ 3. Add the onClick handler */}
                          <Button 
                            onClick={() => setSelectedProduct(product)}
                            className="bg-slate-700 hover:bg-slate-600 text-white px-6 py-2"
                          >
                            Show Details
                          </Button>
                        </div>
                        <Button variant="ghost" size="icon" className="absolute top-4 right-4 bg-white hover:bg-gray-100 text-white rounded-full">
                          <ShoppingBag className="h-4 w-4 text-[#3D4D5C]" />
                        </Button>
                      </div>
                      <div className="p-4">
                        <h3 className="text-white font-medium text-sm mb-2 line-clamp-2 h-10">{product.name}</h3>
                        <p className="text-white font-semibold">{product.price}</p>
                      </div>
                    </CardContent>
                </Card>
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
      
        {/* These components automatically render the previous/next arrow buttons */}
        <CarouselPrevious className="ml-16 text-[#3D4D5C]" />
        <CarouselNext className="mr-16 text-[#3D4D5C]" />
      </Carousel>
        }
    </div>
    <ProductDetailDialog
    isOpen={!!selectedProduct}
    onOpenChange={(isOpen) => {
      if (!isOpen) {
        setSelectedProduct(null); // Close the dialog by clearing the selected product
      }
    }}
    // Pass the full product object, handling the case where it might be null
    product={selectedProduct ? { ...selectedProduct, mainImage: selectedProduct.image } : null}
  />
  </>
  )
}